import { lazy as reactLazy, ComponentType, LazyExoticComponent } from 'react';

/**
 * Wraps React.lazy so that when a hashed chunk fails to load (e.g. after a new
 * deploy invalidated the previously-served chunk names), we retry once and
 * then force a hard reload so the browser fetches the fresh index.html and
 * its updated chunk references. This prevents the "Failed to fetch
 * dynamically imported module" white-screen that hits users with a tab open
 * across deploys.
 */
export function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return reactLazy(async () => {
    const sessionKey = `chunkReloaded:${factory.toString().slice(0, 80)}`;
    try {
      return await factory();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const isChunkError =
        /Failed to fetch dynamically imported module/i.test(message) ||
        /Importing a module script failed/i.test(message) ||
        /ChunkLoadError/i.test(message) ||
        /Loading chunk \d+ failed/i.test(message);

      if (isChunkError && !sessionStorage.getItem(sessionKey)) {
        sessionStorage.setItem(sessionKey, '1');
        window.location.reload();
        // Return a never-resolving promise so React waits for the reload
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}
